/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, List, SegmentedControl, Text, Textarea } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { SearchAutocomplete } from '@/shared/ui/components/form/SearchAutocomplete';
import { useAgencyClients } from '@/features/visits/hooks/useAgencyClients';
import { useAgencyProperties } from '@/features/visits/hooks/useAgencyProperties';
import type { Deal } from '@/services/requests/housien-services/DealsService';
import type { DeclaredInterest } from './CreateDealDialog';
import { DealDrawerSubmitButton } from './DealDrawerSubmitButton';

/** The fields this form can change — exactly what `PUT /deals/{id}` takes. */
export interface EditAttendanceFormData {
  broker_id?: string;
  client_id?: string;
  property_ids?: string[];
  notes?: string;
  interest?: DeclaredInterest;
  deal_type?: DealType;
}

/** What the attendance turned out to be, once anybody has said. */
export type DealType = 'sale' | 'rental';

/** The type as the form holds it: `''` is "nobody has said yet". */
type DealTypeChoice = DealType | '';

/**
 * Whether the type may still be corrected here.
 *
 * The offers govern it. With none recorded the stored value is a guess
 * like any other -- and it is usually the *dialog's* guess, since
 * recording an offer defaults the type from the declared interest -- so
 * it must be fixable. Once an offer exists the offer decides, and the
 * server refuses a value that disagrees with it
 * (`deal_type_contradicts_an_offer`), so the control is shown read-only
 * rather than as a field that would only earn a 422.
 */
export function canCorrectDealType(deal: Deal): boolean {
  return (deal.proposals ?? []).length === 0;
}

/**
 * The attendance's properties, first being the primary.
 */
function propertiesOf(deal: Deal): string[] {
  if (deal.property_ids?.length) return deal.property_ids;
  return deal.property_id ? [deal.property_id] : [];
}

/**
 * Whether two id lists hold the same values in the same order.
 */
function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * Moves *id* to the front, which is what makes it the primary.
 */
function promote(ids: readonly string[], id: string): string[] {
  return [id, ...ids.filter((p) => p !== id)];
}

/** One assignable broker, drawn from the agency's own member list. */
export interface BrokerOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface EditAttendanceDialogProps {
  open: boolean;
  onClose: () => void;
  agencyId: string;
  deal: Deal;
  /** Agency members offered in the responsible-broker picker. */
  brokers: readonly BrokerOption[];
  /** False hides the broker picker — see `canReassignBroker`. */
  canReassign: boolean;
  onSubmit: (data: EditAttendanceFormData) => Promise<void>;
}

const labelClasses = cn(
  'text-[length:var(--text-meta)]',
  'font-[weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

/**
 * A blank string means "cleared", which the wire expresses as absent.
 */
function trimmedOrUndefined(value: string): string | undefined {
  const cleaned = value.trim();
  return cleaned === '' ? undefined : cleaned;
}

/** The edited field values the dialog holds, before diffing against the deal. */
interface EditAttendanceNext {
  brokerId: string;
  clientId: string | null;
  propertyIds: string[];
  notes: string;
  interest: DeclaredInterest;
  dealType: DealTypeChoice;
}

/**
 * The broker patch, when the picker names a different, non-empty broker.
 *
 * @param deal - The stored deal.
 * @param next - The edited field values.
 * @returns The `broker_id` fragment, or empty when unchanged.
 */
function brokerPatch(deal: Deal, next: EditAttendanceNext): EditAttendanceFormData {
  return next.brokerId && next.brokerId !== deal.broker_id
    ? { broker_id: next.brokerId }
    : {};
}

/**
 * The client patch. Handing the attendance to another client is a
 * correction; emptying it reaches the state creation refuses, so a
 * cleared picker is read as "unchanged" rather than sent as a removal
 * the server would 400.
 *
 * @param deal - The stored deal.
 * @param next - The edited field values.
 * @returns The `client_id` fragment, or empty when unchanged.
 */
function clientPatch(deal: Deal, next: EditAttendanceNext): EditAttendanceFormData {
  return next.clientId && next.clientId !== (deal.client_id ?? undefined)
    ? { client_id: next.clientId }
    : {};
}

/**
 * The properties patch, sent as the whole list rather than as
 * `property_id`: the form can reorder and remove, which promoting a
 * single id cannot express.
 *
 * @param deal - The stored deal.
 * @param next - The edited field values.
 * @returns The `property_ids` fragment, or empty when unchanged.
 */
function propertiesPatch(deal: Deal, next: EditAttendanceNext): EditAttendanceFormData {
  return !sameList(next.propertyIds, propertiesOf(deal))
    ? { property_ids: next.propertyIds }
    : {};
}

/**
 * The notes patch, comparing the trimmed text with the stored value.
 *
 * @param deal - The stored deal.
 * @param next - The edited field values.
 * @returns The `notes` fragment, or empty when unchanged.
 */
function notesPatch(deal: Deal, next: EditAttendanceNext): EditAttendanceFormData {
  return trimmedOrUndefined(next.notes) !== (deal.notes ?? undefined)
    ? { notes: trimmedOrUndefined(next.notes) }
    : {};
}

/**
 * The interest patch. Editable on purpose, unlike deal_type: the broker
 * learns what the client wants, and this field is meant to follow.
 *
 * @param deal - The stored deal.
 * @param next - The edited field values.
 * @returns The `interest` fragment, or empty when unchanged.
 */
function interestPatch(deal: Deal, next: EditAttendanceNext): EditAttendanceFormData {
  return next.interest !== (deal.interest ?? 'unsure')
    ? { interest: next.interest }
    : {};
}

/**
 * The deal-type patch. Only ever sent as a correction: `''` means the
 * broker left it undecided, and there is no way to write undecided back
 * -- the request layer refuses a null against this field.
 *
 * @param deal - The stored deal.
 * @param next - The edited field values.
 * @returns The `deal_type` fragment, or empty when unchanged.
 */
function dealTypePatch(deal: Deal, next: EditAttendanceNext): EditAttendanceFormData {
  return next.dealType && next.dealType !== deal.deal_type
    ? { deal_type: next.dealType }
    : {};
}

/**
 * Builds the patch to send, holding only the fields that actually moved.
 *
 * A field the broker did not touch is left out entirely rather than sent
 * back at its current value: the server refuses a `deal_type` an offer
 * disagrees with, so echoing untouched fields would turn an unrelated
 * edit into a refusal.
 */
function changedFields(deal: Deal, next: EditAttendanceNext): EditAttendanceFormData {
  return {
    ...brokerPatch(deal, next),
    ...clientPatch(deal, next),
    ...propertiesPatch(deal, next),
    ...notesPatch(deal, next),
    ...interestPatch(deal, next),
    ...dealTypePatch(deal, next),
  };
}

/**
 * Venda or Aluguel, editable only while no offer has decided it.
 *
 * The attendance keeps a separate `interest` above -- what the broker
 * reads the client as wanting -- and the two are deliberately not the
 * same field. This one is what the attendance turned out to be, and
 * everything downstream of a win branches on it: which checklist the
 * closing tab shows, and whether the win opens a Venda or a Locação.
 *
 * @param props - The deal, the current choice and the change callback.
 * @returns The type control, or its read-only reading once offers exist.
 */
function DealTypeField({ deal, value, onChange }: {
  deal: Deal;
  value: DealTypeChoice;
  onChange: (value: DealType) => void;
}) {
  const { t } = useTranslation('features/deals');
  const editable = canCorrectDealType(deal);

  return (
    <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
      <Text as="label" className={labelClasses}>
        {t('detail.editDialog.dealType')}
      </Text>
      {editable ? (
        <SegmentedControl
          value={value}
          onChange={(v) => onChange(v as DealType)}
          items={[
            { value: 'sale', label: t('sale') },
            { value: 'rental', label: t('rental') },
          ]}
          size="md"
          testId="edit-deal-type-selector"
        />
      ) : (
        <Text
          component="span"
          data-testid="edit-deal-type-locked"
          className="text-[length:var(--text-body-sm)] text-(--text-primary)"
        >
          {deal.deal_type
            ? t(`types.${deal.deal_type}`)
            : t('detail.editDialog.dealTypeUndecided')}
        </Text>
      )}
      <Text
        component="span"
        className="text-[length:var(--text-meta)] leading-normal text-(--text-tertiary)"
      >
        {t(editable
          ? 'detail.editDialog.dealTypeHint'
          : 'detail.editDialog.dealTypeLockedHint')}
      </Text>
    </fieldset>
  );
}

/**
 * Edit form for a running attendance: responsible broker, client,
 * property and notes.
 *
 * Every field here is one `PUT /deals/{id}` already accepts. The screen
 * had no edit path at all, which left a visit-born attendance stuck with
 * whatever client and property the booking guessed — the reason a client
 * who visited one property and preferred another had nowhere to go.
 *
 * The stage is deliberately absent: it is derived from the recorded
 * facts, so it is corrected by correcting the fact.
 *
 * @param props - Dialog state, the deal, broker options and the submit.
 * @returns The edit form shell.
 */
export function EditAttendanceDialog({
  open, onClose, agencyId, deal, brokers, canReassign, onSubmit,
}: EditAttendanceDialogProps) {
  const { t } = useTranslation('features/deals');
  const [brokerId, setBrokerId] = useState(deal.broker_id ?? '');
  const [clientId, setClientId] = useState<string | null>(deal.client_id ?? null);
  const [propertyIds, setPropertyIds] = useState<string[]>(propertiesOf(deal));
  const [interest, setInterest] = useState<DeclaredInterest>(
    (deal.interest as DeclaredInterest | null) ?? 'unsure',
  );
  const [notes, setNotes] = useState(deal.notes ?? '');
  const [dealType, setDealType] = useState<DealTypeChoice>(
    (deal.deal_type as DealTypeChoice | null) ?? '',
  );
  const [submitting, setSubmitting] = useState(false);

  const { clients } = useAgencyClients(open ? agencyId : undefined);
  const { properties } = useAgencyProperties(open ? agencyId : undefined);
  const propertyLabels = useMemo(
    () => new Map(properties.map((p) => [p.id, p.label])),
    [properties],
  );

  useEffect(() => {
    if (!open) return;
    setBrokerId(deal.broker_id ?? '');
    setClientId(deal.client_id ?? null);
    setPropertyIds(propertiesOf(deal));
    setInterest((deal.interest as DeclaredInterest | null) ?? 'unsure');
    setNotes(deal.notes ?? '');
    setDealType((deal.deal_type as DealTypeChoice | null) ?? '');
  }, [open, deal]);

  const patch = changedFields(deal, {
    brokerId, clientId, propertyIds, notes, interest, dealType,
  });
  const isDirty = Object.keys(patch).length > 0;

  const handleSubmit = async () => {
    if (!isDirty) return;
    setSubmitting(true);
    try {
      await onSubmit(patch);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('detail.editDialog.title')}
      density="compact"
      footer={
        <DealDrawerSubmitButton
          onClick={handleSubmit}
          disabled={submitting || !isDirty}
          loading={submitting}
          label={t('detail.editDialog.confirm')}
          savingLabel={t('detail.editDialog.confirming')}
        />
      }
    >
      <div data-testid="edit-attendance-form" className="flex flex-col gap-(--space-4)">
        {canReassign && (
          <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
            <Text as="label" className={labelClasses}>
              {t('detail.editDialog.broker')}
            </Text>
            <SearchAutocomplete
              options={brokers as BrokerOption[]}
              value={brokerId || null}
              onChange={(id) => setBrokerId(id ?? '')}
              placeholder={t('detail.editDialog.brokerPlaceholder')}
            />
          </fieldset>
        )}

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClasses}>
            {t('detail.editDialog.client')}
          </Text>
          <SearchAutocomplete
            options={clients.map((c) => ({ id: c.id, label: c.label, sublabel: c.sublabel }))}
            value={clientId}
            onChange={setClientId}
            placeholder={t('detail.editDialog.clientPlaceholder')}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClasses}>
            {t('form.interest')}
          </Text>
          <SegmentedControl
            value={interest}
            onChange={(v) => setInterest(v as DeclaredInterest)}
            items={[
              { value: 'buy', label: t('form.interests.buy') },
              { value: 'rent', label: t('form.interests.rent') },
              { value: 'both', label: t('form.interests.both') },
              { value: 'unsure', label: t('form.interests.unsure') },
            ]}
            size="md"
            testId="edit-interest-selector"
          />
        </fieldset>

        <DealTypeField
          deal={deal}
          value={dealType}
          onChange={setDealType}
        />

        <fieldset className="flex flex-col gap-(--space-2) border-none p-0 m-0">
          <Text as="label" className={labelClasses}>
            {t('detail.editDialog.properties')}
          </Text>

          {propertyIds.length > 0 && (
            <List type="unordered" spacing="xs" className="list-none p-0 m-0">
              {propertyIds.map((id, index) => (
                <List.Item
                  key={id}
                  data-testid="interest-property-row"
                  className={cn(
                    'flex items-center gap-(--space-2) rounded-(--radius-sm)',
                    'border border-(--border-subtle) px-(--space-2-5) py-(--space-1-5)',
                  )}
                >
                  <Text component="span" className="min-w-0 flex-1 truncate text-[length:var(--text-body-sm)]">
                    {propertyLabels.get(id) ?? id}
                  </Text>
                  {index === 0 ? (
                    <Text
                      component="span"
                      className={cn(
                        'rounded-full border border-(--seed-base)/30 bg-(--seed-base)/8',
                        'px-(--space-2) py-(--space-1) text-[length:var(--text-tag)] uppercase',
                        'tracking-(--tracking-chip) text-(--seed-base)',
                      )}
                    >
                      {t('detail.editDialog.primary')}
                    </Text>
                  ) : (
                    <Button
                      variant="ghost-text"
                      size="micro"
                      onClick={() => setPropertyIds(promote(propertyIds, id))}
                      data-testid="make-primary-button"
                    >
                      {t('detail.editDialog.makePrimary')}
                    </Button>
                  )}
                  <Button
                    variant="ghost-text"
                    size="micro"
                    onClick={() => setPropertyIds(propertyIds.filter((p) => p !== id))}
                    data-testid="remove-property-button"
                  >
                    {t('detail.editDialog.removeProperty')}
                  </Button>
                </List.Item>
              ))}
            </List>
          )}

          {/* Adds rather than replaces, and resets to null so the field is
              ready for the next one instead of showing the last pick. */}
          <SearchAutocomplete
            options={properties
              .filter((p) => !propertyIds.includes(p.id))
              .map((p) => ({ id: p.id, label: p.label, sublabel: p.sublabel }))}
            value={null}
            onChange={(id) => {
              if (id && !propertyIds.includes(id)) setPropertyIds([...propertyIds, id]);
            }}
            placeholder={t('detail.editDialog.addPropertyPlaceholder')}
          />
          <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
            {t('detail.editDialog.propertyHint')}
          </Text>
        </fieldset>

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClasses} htmlFor="edit-attendance-notes">
            {t('detail.editDialog.notes')}
          </Text>
          {/* Textarea, not TextAreaInput: the latter always renders
              InputFill, which reads a form store and throws "Missing
              StoreProvider" outside one. This dialog holds its own state,
              so it needs the primitive rather than the store-backed field. */}
          <Textarea
            id="edit-attendance-notes"
            data-testid="edit-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('detail.editDialog.notesPlaceholder')}
          />
        </fieldset>
      </div>
    </AdaptiveFormShell>
  );
}
